import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useHasDebts } from '@/modules/contacts/hooks/useDebts';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { checkPermission, selectPermissionsState } from '@/redux/permissions/selectors';
import { type Features } from '@/shared/types/account';
import { RootState } from '@/shared/types/state';

import { PAGE_REGISTRY } from '../pageRegistry';
import { type PageEntry } from '../types';

export const isPageVisible = (
  page: PageEntry,
  permissions: RootState['permissions'],
  featureFlags: Features
): boolean => {
  if (page.featureFlag && !featureFlags[page.featureFlag]) {
    return false;
  }
  if (!page.requiredPermission) {
    return true;
  }
  const { module, action } = page.requiredPermission;
  const modules = typeof module === 'string' ? [module] : module;
  return modules.some((m) => checkPermission(permissions, m, action));
};

/** PAGE_REGISTRY filtered to entries the current user has permission to see. */
export const useVisiblePages = (): readonly PageEntry[] => {
  const permissions = useSelector(selectPermissionsState);
  const featureFlags = useSelector(selectFeatureFlags);
  const hasDebts = useHasDebts();

  return useMemo(() => {
    const visible = PAGE_REGISTRY.filter(
      (page) => isPageVisible(page, permissions, featureFlags) && (page.id !== 'debts' || hasDebts)
    );
    return visible.filter(
      (page) =>
        !page.hideWithoutChildren || visible.some((child) => child.parentI18nKey === page.i18nKey)
    );
  }, [permissions, featureFlags, hasDebts]);
};
